import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  Box,
  LinearProgress,
  Fade,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

const CustomCard = ({
  title,
  subtitle,
  icon: Icon,
  progress,
  delay = 0,
  children,
  actions,
  size,
  elevation = 0,
  hoverEffect = true,
  border = true,
  accentColor,
  onClick,
  headerPadding = true,
  contentPadding = true,
  contentStyle = {},
  sx = {},
  ...props
}) => {
  const theme = useTheme();

  const sizeStyles = {
    sm: { minHeight: 90 },
    md: { minHeight: 140 },
    lg: { minHeight: 200 },
  };

  const resolvedSize = typeof size === "string" ? sizeStyles[size] || {} : size;

  const resolvedAccent =
    accentColor && theme.palette[accentColor]
      ? theme.palette[accentColor].main
      : accentColor;

  return (
    <Fade in timeout={300} style={{ transitionDelay: `${delay}ms`, width: "100%", height: "100%" }}>
      <Card
        elevation={elevation}
        onClick={onClick}
        sx={{
          ...resolvedSize,
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.75),
          backdropFilter: "blur(10px)",
          border: border ? `1px solid ${alpha(theme.palette.divider, 0.6)}` : "none",
          transition: theme.transitions.create(["box-shadow", "transform", "border-color"], {
            duration: theme.transitions.duration.short,
          }),
          cursor: onClick ? "pointer" : "default",
          position: "relative",
          overflow: "hidden",

          ...(hoverEffect && {
            "&:hover": {
              boxShadow: onClick ? theme.shadows[4] : theme.shadows[1],
              transform: onClick ? "translateY(-2px)" : "none",
              borderColor: onClick ? alpha(theme.palette.primary.main, 0.4) : alpha(theme.palette.divider, 0.6),
            },
          }),
          ...sx,
        }}
        {...props}
      >
        {/* Sleeker Accent Strip */}
        {resolvedAccent && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              left: 0,
              height: 2,
              backgroundColor: resolvedAccent,
            }}
          />
        )}

        {(title || subtitle) && (
          <CardHeader
            sx={{
              px: headerPadding ? 2 : 0,
              pt: 2,
              pb: subtitle ? 0.5 : 1,
            }}
            title={
              <Typography 
                sx={{ 
                  fontSize: "0.75rem", 
                  fontWeight: 700, 
                  color: "text.secondary", 
                  textTransform: "uppercase", 
                  letterSpacing: "0.1em" 
                }}
              >
                {title}
              </Typography>
            }
            subheader={
              subtitle && (
                <Typography variant="caption" color="text.disabled" sx={{ mt: 0.2 }}>
                  {subtitle}
                </Typography>
              )
            }
            action={
              Icon && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                  }}
                >
                  <Icon sx={{ fontSize: "1rem" }} />
                </Box>
              )
            }
          />
        )}

        <CardContent
          sx={{
            px: contentPadding ? 2 : 0,
            pt: title ? 1 : 2,
            pb: 2,
            "&:last-child": { pb: 2 }, 
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              ...contentStyle,
            }}
          >
            {children}

            {progress !== undefined && (
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  mt: 0.5,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 2,
                  },
                }}
              />
            )}
          </Box>
        </CardContent>

        {actions && (
          <CardActions
            sx={{
              px: 2,
              pb: 1.5,
              pt: 0,
              justifyContent: "flex-end",
            }}
          >
            {actions}
          </CardActions>
        )}
      </Card>
    </Fade>
  );
};

export default CustomCard;